import { jsx as _jsx } from "react/jsx-runtime";
import { App } from "antd";
import $ from "jquery";
import { useCallback, useMemo } from "react";
import { RatingPointer, isRatable } from "@devowl-wp/real-utils";
import { FILTER_SELECTOR } from "../../backbone/filter.js";
import { useAppTreeControllerRef } from "../../contexts/appTreeControllerContext.js";
import { useAppTreeLocalState } from "../../contexts/appTreeLocalStateContext.js";
import { ID_NONE } from "../../integrations/defaultFolder.js";
import { useStores } from "../../store/rootStore.js";
import { TreeStore } from "../../store/treeStore.js";
import { __, _i } from "../../utils/i18n.js";
import { ICON_OBJ_FOLDER_CLOSED, ICON_OBJ_FOLDER_COLLECTION, ICON_OBJ_FOLDER_GALLERY, ICON_OBJ_FOLDER_OPEN, resolveIcon } from "../../utils/icons.js";
import { rmlOpts } from "../../utils/rmlOpts.js";
const CREATABLE_TYPE_INT = {
    folder: 0,
    collection: 1,
    gallery: 2
};
function resolveCreatableTypeInt(typeKey, draft) {
    if (typeof draft?.typeInt === "number" && Number.isFinite(draft.typeInt)) {
        return draft.typeInt;
    }
    if (typeKey && typeKey in CREATABLE_TYPE_INT) {
        return CREATABLE_TYPE_INT[typeKey];
    }
    return 0;
}
function useCreatable() {
    const { message } = App.useApp();
    const { treeStore } = useStores();
    const controllerRef = useAppTreeControllerRef();
    const { setShowProFeature } = useAppTreeLocalState();
    const prepareCreatableDraft = useCallback((type)=>{
        if (type === "collection" && !rmlOpts.others.isPro) {
            setShowProFeature("collections");
            return undefined;
        }
        const typeInt = resolveCreatableTypeInt(type);
        const icons = {
            folder: resolveIcon(ICON_OBJ_FOLDER_CLOSED),
            collection: resolveIcon(ICON_OBJ_FOLDER_COLLECTION),
            gallery: resolveIcon(ICON_OBJ_FOLDER_GALLERY)
        };
        const iconActive = {
            folder: resolveIcon(ICON_OBJ_FOLDER_OPEN)
        };
        return {
            $rename: true,
            icon: icons[type],
            iconActive: iconActive[type],
            parent: +rmlOpts.others.rootId,
            typeInt
        };
    }, [
        setShowProFeature
    ]);
    const handleCreatablePersist = useCallback(async (name, _node, meta)=>{
        const { parentId, draft, type } = meta;
        const typeInt = resolveCreatableTypeInt(type, draft);
        const parent = (+parentId > 0 ? +parentId : undefined) ?? draft?.parent ?? treeStore.rootId;
        if (process.env.PLUGIN_CTX === "lite" && parent !== treeStore.rootId) {
            setShowProFeature("subfolder");
            return false;
        }
        const newObj = await treeStore.persist(name, {
            parent: parent,
            typeInt
        });
        isRatable(rmlOpts.slug) && controllerRef.current.ref && new RatingPointer(rmlOpts.slug, $(controllerRef.current.ref.container).find(".aiot-tree-headline"));
        $(FILTER_SELECTOR).each(function() {
            const backboneFilter = $(this).data("backboneView");
            if (backboneFilter) {
                const { lastSlugs } = backboneFilter;
                lastSlugs.names.push(`(NEW) ${name}`);
                lastSlugs.slugs.push(newObj.id);
                lastSlugs.types.push(typeInt);
                backboneFilter.createFilters(lastSlugs, treeStore);
            }
        });
        controllerRef.current.refreshDroppable?.();
    }, [
        controllerRef,
        setShowProFeature,
        treeStore
    ]);
    const selectedCreatableType = treeStore.selected?.properties?.type;
    const creatableButtons = useMemo(()=>{
        const visibleInFolderType = {
            folder: [
                undefined,
                0,
                4
            ],
            collection: [
                undefined,
                0,
                1,
                4
            ],
            gallery: [
                1
            ]
        };
        const available = rmlOpts.others.isPro || rmlOpts.others.showProHints ? [
            "folder",
            "collection",
            "gallery"
        ] : [
            "folder"
        ];
        const buttons = {};
        for (const key of available){
            const visible = visibleInFolderType[key]?.indexOf(selectedCreatableType) > -1;
            if (key === "gallery" && !visible) {
                continue;
            }
            if (key === "folder") {
                buttons.folder = {
                    icon: resolveIcon(ICON_OBJ_FOLDER_CLOSED),
                    iconActive: resolveIcon(ICON_OBJ_FOLDER_OPEN),
                    cssClasses: "page-title-action add-new-h2",
                    toolTipTitle: __("Click this to create a new folder"),
                    toolTipText: __("A folder can contain any file type and collection, but not galleries. If you want to create a subfolder, select a folder and click this button."),
                    label: "+",
                    visible
                };
            } else if (key === "collection") {
                buttons.collection = {
                    icon: resolveIcon(ICON_OBJ_FOLDER_COLLECTION),
                    cssClasses: "page-title-action add-new-h2",
                    toolTipTitle: __("Click this to create a new collection"),
                    toolTipText: _i(__("A collection cannot contain files. However, you can create additional collections and {{strong}}galleries{{/strong}} there. This gallery is only a {{strong}}gallery data folder{{/strong}}, i.e. they are not automatically visible on the website.\n\nYou can create a {{strong}}visual gallery{{/strong}} by using a shortcode in the Visual Editor on your pages/postings."), {
                        strong: /*#__PURE__*/ _jsx("strong", {})
                    }),
                    label: "+",
                    visible
                };
            } else if (key === "gallery") {
                buttons.gallery = {
                    icon: resolveIcon(ICON_OBJ_FOLDER_GALLERY),
                    cssClasses: "page-title-action add-new-h2",
                    toolTipTitle: _i(__("Click this to create a {{strong}}new gallery data folder{{/strong}}"), {
                        strong: /*#__PURE__*/ _jsx("strong", {})
                    }),
                    toolTipText: _i(__("A {{strong}}gallery data folder{{/strong}} can only contain images. It is easier for you to distinguish where your visual galleries are located.\n\nYou can also order the images into {{strong}}a custom image order{{/strong}} per drag&drop."), {
                        strong: /*#__PURE__*/ _jsx("strong", {})
                    }),
                    label: "+",
                    visible
                };
            }
        }
        return buttons;
    }, [
        selectedCreatableType
    ]);
    return {
        prepareCreatableDraft,
        handleCreatablePersist,
        creatableButtons,
        getCreatableSelectedId: ()=>treeStore.selectedId,
        getCreatableRootPlacementIds: ()=>[
                TreeStore.ID_ALL,
                +rmlOpts.others.rootId,
                ID_NONE
            ],
        onCreatableStart: (inputValue)=>message.loading(_i(__("Creating {{strong}}%s{{/strong}}...", inputValue), {
                strong: /*#__PURE__*/ _jsx("strong", {})
            })),
        onCreatableSuccess: (name)=>message.success(_i(__("Successfully created {{strong}}%s{{/strong}}", name), {
                strong: /*#__PURE__*/ _jsx("strong", {})
            })),
        onCreatableError: (error)=>message.error(error.responseJSON.message)
    };
}
export { useCreatable };
